import {get} from 'object-path-immutable'
import React, {useReducer, Reducer, useCallback, ReactNode} from 'react'
import {IconDropdown} from '../dropdown/dropdown'
import {Button} from '../buttons/button'
import {Icon, IconName} from '../icon/icon'
import styled, {css} from 'styled-components'
import {Loader} from '../../loader'

type HorizontalAlign = 'left' | 'right' | 'center'

interface BaseColumn<OrderBy> {
  header?: ReactNode
  sortKey?: OrderBy
  sortDesc?: boolean
  horizontalAlign?: HorizontalAlign
}

interface StringColumn<OrderBy> extends BaseColumn<OrderBy> {
  id?: string
  accessor: string
}

interface FunctionColumn<Row, OrderBy> extends BaseColumn<OrderBy> {
  id: string // id required since accessor can't be used as a key
  accessor: (row: Row) => React.ReactNode
}

export type ListColumn<Row, OrderBy> =
  | StringColumn<OrderBy>
  | FunctionColumn<Row, OrderBy>

export type ListColumns<
  Row extends BaseRow,
  OrderBy extends string = string
> = ListColumn<Row, OrderBy>[]

export interface BaseRow {
  id: string | number
  hasOverflow?: boolean
}

// remove these once ListQuery is gone
// type OrderBy = string
type SetOrderBy<OrderBy extends string> = (props: OrderBy) => void
type FetchMore = () => void

const columnKey = <Row extends BaseRow, OrderBy extends string>(
  c: ListColumn<Row, OrderBy>
) => ('id' in c ? c.id : c.accessor)

const getCellContent = <Row extends BaseRow, OrderBy extends string>(
  c: ListColumn<Row, OrderBy>,
  row: Row
) => {
  if (typeof c.accessor === 'string') {
    return get(row, c.accessor)
  } else {
    return c.accessor(row)
  }
}

export interface ListProps<Row extends BaseRow, OrderBy extends string> {
  id?: string
  displayHeader?: boolean
  columns: ListColumns<Row, OrderBy>
  rows?: Row[]
  horizontalAlign?: string
  renderOverflow?: (row: Row, closeDropdown: () => void) => React.ReactNode
  overFlowDisabled?: (row: Row) => boolean
  renderEmpty?: () => React.ReactNode
  fetchMore?: FetchMore
  setOrderBy?: SetOrderBy<OrderBy>
  orderBy?: OrderBy[]
  loading?: boolean
  bleed?: string
  grid?: boolean
  getRowClassName?: (row: Row) => string | undefined
  loadingMore?: boolean
  className?: string
  onClickRow?: (row: Row) => void
}

export const List = <Row extends BaseRow, OrderBy extends string = string>(
  props: ListProps<Row, OrderBy>
) => {
  const {
    columns,
    rows,
    renderOverflow,
    fetchMore,
    setOrderBy,
    orderBy,
    displayHeader = true,
    loading,
    renderEmpty = () => <NoResults />,
    bleed = '',
    grid = false,
    getRowClassName,
    onClickRow,
    className,
    loadingMore
  } = props

  const toggleSort = useCallback(
    (sortKey: OrderBy) =>
      // toggle direction if this is the current primary order
      // otherwise use default
      setOrderBy
        ? setOrderBy(
            // need to cast because we assume this creates a valid OrderBy value,
            // but TS has no way to determine that
            (sortKey.toUpperCase().replace(/_(ASC|DESC)$/, '') +
              (orderBy &&
              orderBy[0] &&
              orderBy[0].indexOf(sortKey) === 0 &&
              orderBy[0].match(/_ASC$/)
                ? '_DESC'
                : '_ASC')) as OrderBy
          )
        : undefined,
    [orderBy, setOrderBy]
  )

  return (
    <TableOuter className={className} id={props.id ?? 'list-table'}>
      <BorderAligner>
        <TableInner $loading={loading}>
          {loading ? (
            <Loader delay={0} style={{marginTop: '40px'}} />
          ) : rows && rows.length ? (
            <Table grid={grid}>
              {displayHeader ? (
                <thead>
                  <tr>
                    {columns.map(c => {
                      const sortKey = c.sortKey
                      return (
                        <StyledHeader
                          key={columnKey(c)}
                          horizontalAlign={c.horizontalAlign}
                        >
                          {setOrderBy && sortKey ? (
                            <StyledSorter
                              onClick={e => {
                                e.preventDefault()
                                toggleSort(sortKey)
                              }}
                            >
                              {c.header}{' '}
                              {orderBy &&
                              orderBy.length &&
                              orderBy[0].indexOf(
                                sortKey.replace(/(?:_DESC|_ASC)$/, '')
                              ) === 0 ? (
                                orderBy[0].match(/_DESC$/) ? (
                                  <SortIcon>
                                    <Icon name="back_small"></Icon>
                                  </SortIcon>
                                ) : (
                                  <SortIcon
                                    style={{transform: 'rotate(90deg)'}}
                                  >
                                    <Icon name="back_small"></Icon>
                                  </SortIcon>
                                )
                              ) : null}
                            </StyledSorter>
                          ) : (
                            c.header
                          )}
                        </StyledHeader>
                      )
                    })}
                    {renderOverflow ? <StyledHeader /> : null}
                  </tr>
                </thead>
              ) : null}
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.id}
                    className={
                      getRowClassName ? getRowClassName(row) : undefined
                    }
                    onClick={onClickRow ? () => onClickRow(row) : undefined}
                  >
                    {columns.map(c => (
                      <td key={columnKey(c)}>
                        <CellContent horizontalAlign={c.horizontalAlign}>
                          {getCellContent(c, row)}
                        </CellContent>
                      </td>
                    ))}
                    {renderOverflow ? (
                      <OverflowCell data-testid={`overflow-${row.id}`}>
                        <RelativePositioner>
                          {!('hasOverflow' in row) || row.hasOverflow ? (
                            <IconDropdown
                              icon="overflow"
                              rotateIcon={grid ? 90 : undefined}
                              appearance={grid ? 'bordered' : undefined}
                              order="down"
                            >
                              {closeDropdown =>
                                renderOverflow(row, closeDropdown)
                              }
                            </IconDropdown>
                          ) : null}
                        </RelativePositioner>
                      </OverflowCell>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyWrapper>
              {loading ? <Loader variant="inline" /> : renderEmpty()}
            </EmptyWrapper>
          )}
        </TableInner>
        {!grid && (
          <FakeBorder bleed={bleed} displayHeader={displayHeader}></FakeBorder>
        )}
      </BorderAligner>
      {fetchMore && !loading ? (
        <LoadMore>
          <Button
            size={'large'}
            appearance={'transparent'}
            disabled={loadingMore}
            onClick={fetchMore}
          >
            {loadingMore ? 'Loading..' : 'Load more'}
          </Button>
        </LoadMore>
      ) : null}
    </TableOuter>
  )
}

export const EmptyWrapper = styled.div`
  padding: 30px;
  border: 1px solid ${p => p.theme.grey10};
  border-radius: ${p => p.theme.borderRadius};
  z-index: 1; /* To raise above fake left border */
  background: white;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
`

type NoResultsProps = {
  icon?: IconName
  message?: string
  heading?: string
  children?: React.ReactNode
  large?: boolean
}

export const NoResults = ({
  icon,
  message = 'Nothing to show here yet',
  heading,
  children,
  large = false
}: NoResultsProps) => (
  <NoResultsContainer large={large}>
    <EmptyIcon>{icon ? <Icon name={icon}></Icon> : null}</EmptyIcon>
    <EmptyHeading>{heading ? heading : 'No data'}</EmptyHeading>
    {message ? <p>{message}</p> : null}
    {children}
  </NoResultsContainer>
)

export const NoResultsPermissions = ({
  icon = 'padlock',
  heading = 'No access',
  message = "Sorry, you don't have permission to view these records",
  ...props
}: NoResultsProps) => (
  <NoResults icon={icon} message={message} heading={heading} {...props} />
)

export const MutedCell = styled.div`
  color: ${({theme}) => theme.grey50};
`

export const CellContent = styled.div<{horizontalAlign?: HorizontalAlign}>`
  min-height: 58px;
  padding: 9px 20px;
  display: flex;
  align-items: center;
  ${p => {
    switch (p.horizontalAlign) {
      case 'center':
        return `
          text-align: center;
          justify-content: center;
        `
      case 'right':
        return `
          text-align: right;
          justify-content: flex-end;
        `
      default:
        return `
          text-align: left;
          justify-content: flex-start;
        `
    }
  }}
`

const StyledHeader = styled.th<{horizontalAlign?: HorizontalAlign}>`
  ${p => {
    switch (p.horizontalAlign) {
      case 'center':
        return `text-align: center;`
      case 'right':
        return `text-align: right;`
      default:
        return `text-align: left;`
    }
  }}
`

const EmptyIcon = styled.div`
  svg {
    color: ${({theme}) => theme.grey100};
  }
`

const NoResultsContainer = styled.div<{large: boolean}>`
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: ${({theme}) => theme.grey50};
  font-size: ${({theme}) => theme.fontSize};
  line-height: ${({theme}) => theme.lineHeight};
  text-align: center;

  > p {
    max-width: 500px;
    margin: 0 0 5px;
  }
  // Exact height to equal 300px, minus padding on wrapper and border top and bottom
  ${p => (p.large ? `min-height: 238px` : null)}
`
const EmptyHeading = styled.div`
  font-size: ${({theme}) => theme.fontSizeL};
  line-height: ${({theme}) => theme.lineHeightL};
  text-align: center;
  margin: 10px 0;
  max-width: 500px;
  color: ${p => p.theme.black};
`

const TableOuter = styled.div``

const BorderAligner = styled.div`
  position: relative;
`

// creates border around the table body
export const FakeBorder = styled.div<{displayHeader: boolean; bleed: string}>`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  border: 1px solid ${({theme}) => theme.grey10};
  border-radius: ${({theme}) => theme.borderRadius};
  pointer-events: none;
  ${p =>
    p.displayHeader
      ? css`
          height: calc(100% - 40px);
          top: 40px;
        `
      : css`
          height: 100%;
          top: 0;
        `}
  ${p =>
    p.bleed
      ? css`
          box-shadow: 0 0 0 10px ${p.bleed};
        `
      : null}
`

const TableInner = styled.div<{$loading?: boolean}>`
  position: relative;
  max-width: 100%;
  width: 100%;
  ${p =>
    p.$loading ? `overflow: hidden; touch-action:none` : `overflow-x: auto`}
`

const RelativePositioner = styled.div`
  position: relative;
  padding: 0;
  width: 60px;
  min-height: 60px;
  height: 100%;
  box-sizing: content-box;
  display: flex;
  align-items: center;
  justify-content: center;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 100%;
    box-shadow: -1px 0 0 0 ${p => p.theme.grey10};
  }
`

const OverflowCell = styled.td`
  right: 0;
  top: 0;
  width: 60px;
  height: 100%;
  min-height: 60px;
`

const StyledSorter = styled.a`
  &:hover,
  &:focus {
    transition: all 0.3s ease;
    color: ${p => p.theme.black};
  }
`

const SortIcon = styled.div`
  display: inline-flex;
  transform: rotate(-90deg);
  transition: transform 0.3s ease;
`

export const Table = styled.table<{grid: boolean}>`
  pre {
    margin: 0;
  }
  margin: 0;
  width: 100%;
  border-spacing: 0;
  thead {
    th {
      white-space: nowrap;
      padding: 10px 20px 10px;
      font-size: ${p => p.theme.fontSizeXS};
      line-height: ${p => p.theme.lineHeightCompact};
      color: ${p => p.theme.grey50};
      font-weight: normal;
    }
  }
  tbody {
    tr {
      min-height: 60px;
      position: relative;
      background: white;
    }
    tr.selected {
      background: #d3e3ff;
      /*hack this is ${p => p.theme.blueLightest};
      but without opacity, otherwise the last-of-type td is see through */
    }
    td {
      background: inherit;
      white-space: nowrap;
      ${p =>
        !p.grid &&
        css`
          border-bottom: 1px solid ${p.theme.grey10};
        `}
      font-size: ${p => p.theme.fontSizeS};
      &:last-of-type {
        position: sticky;
        padding: 0;
      }
    }
    tr:first-child {
      td {
        &:first-child {
          border-top-left-radius: ${p => p.theme.borderRadius};
        }
        &:last-child {
          border-top-right-radius: ${p => p.theme.borderRadius};
        }
      }
    }
    tr:last-child {
      td {
        border-bottom: none;
        &:first-child {
          border-bottom-left-radius: ${p => p.theme.borderRadius};
        }
        &:last-child {
          border-bottom-right-radius: ${p => p.theme.borderRadius};
        }
      }
    }
    tr:only-of-type {
      td {
        &:first-of-type {
          border-bottom-left-radius: ${p => p.theme.borderRadius};
        }
      }
      td {
        &:last-of-type {
          border-bottom-right-radius: ${p => p.theme.borderRadius};
        }
      }
    }

    tr td:first-child > ${CellContent} a {
      margin: -20px;
      padding: 20px;
      font-weight: bold;

      &:hover,
      &:focus {
        text-decoration: underline;
        outline: none;
      }
    }
  }
  ${p =>
    p.grid &&
    css`
      thead {
        display: none;
      }
      th {
        display: none;
      }
      display: flex;
      td {
        display: flex;
        flex-direction: column;
      }
      tbody {
        width: 100%;
        display: grid;
        grid-gap: 30px;
        grid-template-rows: auto;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      }
      tr {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        border-radius: ${p.theme.borderRadius};
        border: 1px solid ${p.theme.grey10};
        padding: 30px;
      }
      ${CellContent} {
        padding: 0;
        min-height: 0;
      }
      ${OverflowCell} {
        min-height: 0;
        width: auto;
        margin-top: 20px;
      }
      ${RelativePositioner} {
        width: auto;
        min-height: 0;
        &:after {
          box-shadow: none;
        }
      }
    `}
`

const LoadMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
`

const stripOrderBySuffix = (item: string) => item.replace(/_ASC$|_DESC$/, '')

const listOrderByReducer = <T extends string>(
  currentOrderBy: T[],
  newOrderBy: T
): T[] => {
  // push the new order key to the start of the list, and strip any instances
  // of it (asc or desc) from the remainder
  return [newOrderBy].concat(
    currentOrderBy.filter(
      item =>
        // compare without _ASC/_DESC suffix
        stripOrderBySuffix(item) !== stripOrderBySuffix(newOrderBy)
    )
  )
}
export const useListOrderBy = <T extends string>(defaultOrderBy: T[] = []) => {
  // cast seems necessary or TS can't infer the return type of this function
  return useReducer(listOrderByReducer as Reducer<T[], T>, defaultOrderBy)
}

/**
 * Returns a function which updates a query by appending new results to the
 * existing result set. Used as the updateQuery parameter to a fetchMore call.
 * @param operation the name of the graphql operation, e.g. environments
 * @param key key of iterable obects from the query response
 */
export const makeLoadMoreUpdater =
  <T extends {[key: string]: unknown}>(operation: string, key = 'nodes') =>
  (
    previousResult: T,
    {
      fetchMoreResult
    }: {
      fetchMoreResult?: T
      variables?: unknown
    }
  ): T => {
    if (!fetchMoreResult || !fetchMoreResult[operation]) {
      return previousResult
    }

    const nodes = [
      ...((previousResult[operation] as Record<string, unknown[]>)[key] ?? []),
      ...(fetchMoreResult[operation] as Record<string, unknown[]>)[key]
    ]

    return {
      ...fetchMoreResult,
      [operation]: {
        ...(fetchMoreResult[operation] as Record<string, unknown[]>),
        [key]: nodes
      }
    }
  }

export interface ListQueryChildrenProps<Row, OrderBy extends string> {
  rows: Row[]
  setOrderBy: SetOrderBy<OrderBy>
  orderBy: OrderBy[]
  fetchMore?: FetchMore
  refetch?: () => void
}
